import { ChevronDown, ChevronUp, Home, Users } from 'lucide-react';
import { useState } from 'react';
import type { DetailedVote } from '@yawi/types';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';

interface DetailedVotesTableProps {
  detailedVotes: DetailedVote[];
}

export const DetailedVotesTable: React.FC<DetailedVotesTableProps> = ({ detailedVotes }) => {
  const { t } = useI18n();
  const { formatDateTime } = useDateFormatter();
  const [isExpanded, setIsExpanded] = useState(false);

  if (detailedVotes.length === 0) {
    return (
      <p className="text-xs text-slate-400 italic">{t('polls.detailedVotes.empty')}</p>
    );
  }

  return (
    <div className="border-t border-slate-100 pt-3 mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors w-full"
      >
        <Users size={16} className="text-violet-500" />
        <span>{t('polls.detailedVotes')}</span>
        <span className="text-xs text-slate-400">({detailedVotes.length})</span>
        {isExpanded ? <ChevronUp size={16} className="ml-auto" /> : <ChevronDown size={16} className="ml-auto" />}
      </button>

      {isExpanded && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 pr-3 font-medium text-slate-500">{t('polls.detailedVotes.voter')}</th>
                <th className="text-left py-2 pr-3 font-medium text-slate-500">{t('polls.detailedVotes.option')}</th>
                <th className="text-left py-2 pr-3 font-medium text-slate-500">{t('polls.detailedVotes.unit')}</th>
                <th className="text-left py-2 font-medium text-slate-500">{t('polls.detailedVotes.date')}</th>
              </tr>
            </thead>
            <tbody>
              {detailedVotes.map((vote, idx) => (
                <tr key={`${vote.userId}-${vote.optionId}-${idx}`} className="border-b border-slate-50">
                  <td className="py-2 pr-3 text-slate-700">{vote.displayName}</td>
                  <td className="py-2 pr-3">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs">
                      {vote.optionText}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    {vote.unitName ? (
                      <span className="inline-flex items-center gap-1 text-violet-600">
                        <Home size={12} className="shrink-0" />
                        {vote.unitName}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-2 text-slate-400">{formatDateTime(vote.votedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
